import React, { useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    Divider,
    IconButton,
    TextField,
    Button,
    Chip,
    Dialog,
    MenuItem,
    Slider,
} from '@mui/material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { EditRounded, AddCircleOutline, CheckCircleOutline, Cancel } from '@mui/icons-material';
import { IEvent } from '../../../../types/global/helper';
// import { EventDetails } from '../../../EventComponents/EventDetails';
// import { EventDescription } from '../../../EventComponents/EventDescription';
// import { EventHost } from '../../../EventComponents/EventHost';
// import { EventRequirements } from '../../../EventComponents/EventRequirements';
import { useDispatch } from 'react-redux';
import { updateEvent } from '../../../../redux/events/action';
import { CustomDatePicker } from '../../../DatePicker/CustomDatePicker';
import { EventSchedule } from '../../../EventComponents/EventSchedule';
import LocationAutocomplete from '../../../LocationAutocomplete/LocationAutocomplete';
import { formatDateWithTimezone } from '../../../../utils/AppUtils';

const REFUND_POLICY_OPTIONS = ['non-refundable', 'refund up to 7 days before the event', 'refundable at anytime'];

const EVENT_KIND_OPTIONS = [
    'watch party',
    'networking',
    'conference',
    'film festival',
    'music festival',
    'meet up',
    'dance party',
    'creative dinner',
];

const DRESS_CODE_OPTIONS = ['casual', 'business casual', 'formal', 'black tie', 'costume', 'themed'];

interface EventDetailsTabProps {
    event?: IEvent;
    onEditDetails: (sectionName: string) => void;
}

interface EventDateBreakdown {
    start_date: string | Date;
    end_date: string | Date;
    detail?: string;
    location?: {
        type: string;
        coordinates: number[];
        address: string;
        description: string;
        short_name: string;
    };
}

export const EventDetailsTab: React.FC<EventDetailsTabProps> = ({ event, onEditDetails }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();

    // State for editing sections
    const [editingSections, setEditingSections] = useState<{ [key: string]: boolean }>({
        details: false,
        description: false,
        dateLocation: false,
        host: false,
        requirements: false,
        typeTags: false,
        schedule: false,
    });

    // State for edited values
    const [editedValues, setEditedValues] = useState<{ [key: string]: any }>({});

    // State for tags dialog
    const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
    const [newTag, setNewTag] = useState('');
    const [selectedTags, setSelectedTags] = useState<string[]>([]);

    // Common tags
    const commonTags = [
        'Music',
        'Art',
        'Theater',
        'Film',
        'Dance',
        'Comedy',
        'Food',
        'Drinks',
        'Networking',
        'Education',
        'Technology',
        'Workshop',
        'Conference',
        'Festival',
    ];

    // State for requirements
    const [requirements, setRequirements] = useState<string[]>([]);
    const [newRequirement, setNewRequirement] = useState('');

    const noop = () => {};

    if (!event) {
        return (
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                    fontSize: '1rem',
                    color: theme[themeStyle].primary_white,
                }}>
                No event details available
            </Typography>
        );
    }

    const handleStartEditing = (section: string) => {
        // Initialize edited values when starting to edit a section
        let initialValues: any = {};

        switch (section) {
            case 'details':
                initialValues = {
                    name: event.name,
                    eventType: event.kind,
                    refundPolicy: event.refund_policy || '',
                    max_ticket_purchasable_per_person: event.max_ticket_purchasable_per_person || 0,
                    total_tickets: event.total_tickets || 0,
                    age_restriction: event.age_restriction || 0,
                    dress_code: event.dress_code || '',
                };
                break;
            case 'description':
                initialValues = {
                    description: event.description || '',
                };
                break;
            case 'dateLocation':
                initialValues = {
                    startDate: event.event_start_date,
                    endDate: event.event_end_date,
                    location: event.location?.address || '',
                };
                break;
            case 'host':
                initialValues = {
                    aboutHost: event.about_host || '',
                };
                break;
            case 'requirements':
                setRequirements(event.requirements || []);
                break;
            case 'typeTags':
                initialValues = {
                    eventType: event.kind,
                };
                setSelectedTags(event.tags || []);
                break;
            case 'schedule':
                initialValues = {
                    event_date_breakdown: event.event_date_breakdown || [],
                };
                break;
        }

        setEditedValues({ ...editedValues, ...initialValues });
        setEditingSections({ ...editingSections, [section]: true });
    };

    const handleCancelEditing = (section: string) => {
        setEditingSections({ ...editingSections, [section]: false });
    };

    const handleSaveChanges = (section: string) => {
        let updatePayload: any = {};

        switch (section) {
            case 'details':
                updatePayload = {
                    name: editedValues.name,
                    kind: editedValues.eventType,
                    refund_policy: editedValues.refundPolicy,
                    max_ticket_purchasable_per_person: editedValues.max_ticket_purchasable_per_person,
                    total_tickets: editedValues.total_tickets,
                    age_restriction: editedValues.age_restriction,
                    dress_code: editedValues.dress_code,
                };
                break;
            case 'description':
                updatePayload = {
                    description: editedValues.description,
                };
                break;
            case 'dateLocation':
                updatePayload = {
                    event_start_date: editedValues.startDate,
                    event_end_date: editedValues.endDate,
                    timezone: editedValues.timezone,
                };
                if (editedValues.locationCoordinates) {
                    updatePayload.location = editedValues.locationCoordinates;
                }
                break;
            case 'host':
                updatePayload = {
                    about_host: editedValues.aboutHost,
                };
                break;
            case 'requirements':
                updatePayload = {
                    requirements: requirements,
                };
                break;
            case 'typeTags':
                updatePayload = {
                    kind: editedValues.eventType,
                    tags: selectedTags,
                };
                break;
            case 'schedule':
                updatePayload = {
                    event_date_breakdown: editedValues.event_date_breakdown,
                };
                break;
        }

        // Dispatch update event action
        if (event._id) {
            dispatch(
                updateEvent({
                    id: event._id,
                    body: updatePayload,
                }),
            );
        }

        // Turn off editing for this section
        setEditingSections({ ...editingSections, [section]: false });
    };

    const handleInputChange = (field: string, value: any) => {
        setEditedValues({ ...editedValues, [field]: value });
    };

    const handleBreakdownChange = (index: number, field: string, value: any) => {
        const newBreakdown = [...(editedValues.event_date_breakdown || [])];
        newBreakdown[index] = { ...newBreakdown[index], [field]: value };
        handleInputChange('event_date_breakdown', newBreakdown);
    };

    const handleAddBreakdown = () => {
        const newBreakdown = [...(editedValues.event_date_breakdown || [])];
        newBreakdown.push({
            start_date: new Date(),
            end_date: new Date(),
            detail: '',
            location: {
                type: 'Point',
                coordinates: [],
                address: '',
                description: '',
                short_name: '',
            },
        });
        handleInputChange('event_date_breakdown', newBreakdown);
    };

    const handleAddRequirement = () => {
        if (newRequirement.trim()) {
            setRequirements([...requirements, newRequirement.trim()]);
            setNewRequirement('');
        }
    };

    const handleRemoveRequirement = (index: number) => {
        const updatedRequirements = [...requirements];
        updatedRequirements.splice(index, 1);
        setRequirements(updatedRequirements);
    };

    const handleAddTag = () => {
        if (newTag.trim() && !selectedTags.includes(newTag.trim())) {
            setSelectedTags([...selectedTags, newTag.trim()]);
            setNewTag('');
        }
    };

    const handleSelectTag = (tag: string) => {
        if (!selectedTags.includes(tag)) {
            setSelectedTags([...selectedTags, tag]);
        } else {
            setSelectedTags(selectedTags.filter(t => t !== tag));
        }
    };

    const SectionHeader = ({ title, section }: { title: string; section: string }) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: '1.25rem',
                    color: theme[themeStyle].primary_white,
                }}>
                {title}
            </Typography>

            {editingSections[section] ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={() => handleCancelEditing(section)}
                        sx={{
                            borderColor: theme[themeStyle].placeholderGray,
                            color: theme[themeStyle].primary_white,
                            fontSize: '0.875rem',
                            '&:hover': {
                                borderColor: theme[themeStyle].placeholderGray,
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            },
                        }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<CheckCircleOutline sx={{ color: theme[themeStyle].black }} />}
                        onClick={() => handleSaveChanges(section)}
                        sx={{
                            backgroundColor: theme[themeStyle].highlight,
                            color: theme[themeStyle].black,
                            fontSize: '0.875rem',
                            '&:hover': {
                                backgroundColor: theme[themeStyle].primary_orange,
                                opacity: 0.9,
                            },
                        }}>
                        Save
                    </Button>
                </Box>
            ) : (
                <IconButton
                    onClick={() => {
                        handleStartEditing(section);
                        onEditDetails(section);
                    }}
                    sx={{
                        color: theme[themeStyle].primary_orange,
                    }}>
                    <EditRounded />
                </IconButton>
            )}
        </Box>
    );

    return (
        <Box
            sx={{
                py: 3,
                bgcolor: theme[themeStyle].pure_black,
                borderRadius: 2,
                width: '100%',
            }}>
            {/* Event Details */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Event Details" section="details" />

                {!editingSections.details ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Event Name
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.name}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Event Type
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.kind || 'Not specified'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Refund Policy
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.refund_policy || 'Not specified'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Max Tickets Per Person
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.max_ticket_purchasable_per_person || 'No limit'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Total Tickets
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.total_tickets || 'Unlimited'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Age Restriction
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.age_restriction
                                    ? `${event.age_restriction} years and above`
                                    : 'No age restriction'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Dress Code
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.dress_code || 'Not specified'}
                            </Typography>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Event Name
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Event Name"
                                value={editedValues.name || ''}
                                onChange={e => handleInputChange('name', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Event Type
                            </Typography>
                            <TextField
                                select
                                fullWidth
                                value={editedValues.eventType || ''}
                                onChange={e => handleInputChange('eventType', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                    '& .MuiSelect-select': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}>
                                {EVENT_KIND_OPTIONS.map(option => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Refund Policy
                            </Typography>
                            <TextField
                                select
                                fullWidth
                                value={editedValues.refundPolicy || ''}
                                onChange={e => handleInputChange('refundPolicy', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                    '& .MuiSelect-select': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}>
                                {REFUND_POLICY_OPTIONS.map(option => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Max Tickets Per Person
                            </Typography>
                            <TextField
                                type="number"
                                fullWidth
                                value={editedValues.max_ticket_purchasable_per_person || ''}
                                onChange={e =>
                                    handleInputChange('max_ticket_purchasable_per_person', parseInt(e.target.value))
                                }
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Total Tickets
                            </Typography>
                            <TextField
                                type="number"
                                fullWidth
                                value={editedValues.total_tickets || ''}
                                onChange={e => handleInputChange('total_tickets', parseInt(e.target.value))}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Age Restriction
                            </Typography>
                            <Slider
                                value={editedValues.age_restriction || 0}
                                onChange={(_, value) => handleInputChange('age_restriction', value)}
                                min={0}
                                max={100}
                                valueLabelDisplay="auto"
                                sx={{
                                    color: theme[themeStyle].primary_orange,
                                    '& .MuiSlider-thumb': {
                                        backgroundColor: theme[themeStyle].primary_orange,
                                    },
                                }}
                            />
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].light_gray_text,
                                    textAlign: 'center',
                                }}>
                                {editedValues.age_restriction || 0} years and above
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Dress Code
                            </Typography>
                            <TextField
                                select
                                fullWidth
                                value={editedValues.dress_code || ''}
                                onChange={e => handleInputChange('dress_code', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                    '& .MuiSelect-select': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].primary_white,
                                                '& .MuiMenuItem-root': {
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme[themeStyle].primary_orange,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}>
                                {DRESS_CODE_OPTIONS.map(option => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Description */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Description" section="description" />

                {!editingSections.description ? (
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            color: theme[themeStyle].primary_white,
                            whiteSpace: 'pre-wrap',
                        }}>
                        {event.description || 'No description provided'}
                    </Typography>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={6}
                                placeholder="Event Description"
                                value={editedValues.description || ''}
                                onChange={e => handleInputChange('description', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Date & Location */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Date & Location" section="dateLocation" />

                {!editingSections.dateLocation ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Start Date
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.event_start_date
                                    ? formatDateWithTimezone(event.event_start_date, event.timezone)
                                    : 'Not set'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                End Date
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.event_end_date
                                    ? formatDateWithTimezone(event.event_end_date, event.timezone)
                                    : 'Not set'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Location
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.location?.address || 'Not specified'}
                            </Typography>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Start Date
                            </Typography>
                            <CustomDatePicker
                                value={editedValues.startDate ? editedValues.startDate : undefined}
                                timezone={event.timezone}
                                onChange={(date, timezone) => {
                                    handleInputChange('startDate', date);
                                    if (timezone) {
                                        handleInputChange('timezone', timezone);
                                    }
                                }}
                                withTime
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                End Date
                            </Typography>
                            <CustomDatePicker
                                value={editedValues.endDate ? editedValues.endDate : undefined}
                                timezone={event.timezone}
                                onChange={(date, timezone) => {
                                    handleInputChange('endDate', date);
                                    if (timezone) {
                                        handleInputChange('timezone', timezone);
                                    }
                                }}
                                withTime
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Location
                            </Typography>
                            <LocationAutocomplete
                                value={editedValues.locationCoordinates}
                                onSelect={(address, coordinates, formattedAddress) => {
                                    handleInputChange('location', formattedAddress);
                                    handleInputChange('locationCoordinates', formattedAddress);
                                }}
                                onSelectLocationOption={noop}
                                placeholder="Search Places ..."
                                withCordinates
                            />
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* About Host */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="About Host" section="host" />

                {!editingSections.host ? (
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            color: theme[themeStyle].primary_white,
                            whiteSpace: 'pre-wrap',
                        }}>
                        {event.about_host || 'No host information provided'}
                    </Typography>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="About the Host"
                                value={editedValues.aboutHost || ''}
                                onChange={e => handleInputChange('aboutHost', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Requirements */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Requirements" section="requirements" />

                {!editingSections.requirements ? (
                    <Box>
                        {event.requirements && event.requirements.length > 0 ? (
                            event.requirements.map((req, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        backgroundColor: theme[themeStyle].gray_text,
                                        padding: 2,
                                        borderRadius: 1,
                                        mb: 1,
                                    }}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].primary_white,
                                        }}>
                                        {req}
                                    </Typography>
                                </Box>
                            ))
                        ) : (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].light_gray_text,
                                    fontStyle: 'italic',
                                }}>
                                No requirements added yet
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                                <TextField
                                    fullWidth
                                    placeholder="Add a requirement"
                                    value={newRequirement}
                                    onChange={e => setNewRequirement(e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: theme[themeStyle].tag_white,
                                            backgroundColor: theme[themeStyle].gray_text,
                                        },
                                    }}
                                    onKeyPress={e => {
                                        if (e.key === 'Enter') {
                                            handleAddRequirement();
                                            e.preventDefault();
                                        }
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleAddRequirement}
                                    sx={{
                                        backgroundColor: theme[themeStyle].primary_orange,
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].primary_orange,
                                            opacity: 0.9,
                                        },
                                    }}>
                                    Add
                                </Button>
                            </Box>

                            <Box>
                                {requirements.map((req, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            backgroundColor: theme[themeStyle].gray_text,
                                            padding: 2,
                                            borderRadius: 1,
                                            mb: 1,
                                        }}>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                color: theme[themeStyle].primary_white,
                                            }}>
                                            {req}
                                        </Typography>
                                        <IconButton
                                            onClick={() => handleRemoveRequirement(index)}
                                            sx={{
                                                color: theme[themeStyle].placeholderGray,
                                            }}>
                                            <Cancel />
                                        </IconButton>
                                    </Box>
                                ))}

                                {requirements.length === 0 && (
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].light_gray_text,
                                            fontStyle: 'italic',
                                        }}>
                                        No requirements added yet
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Box>

            <Divider sx={{ my: 4, borderColor: theme[themeStyle].form_black }} />

            {/* Event Type & Tags */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Event Type & Tags" section="typeTags" />

                {!editingSections.typeTags ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Event Type
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                {event.kind || 'Not specified'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Tags
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {event.tags && event.tags.length > 0 ? (
                                    event.tags.map((tag, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                backgroundColor: theme[themeStyle].background_gradient.end,
                                                borderRadius: 1,
                                                padding: '4px 8px',
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                fontSize: '0.875rem',
                                                color: theme[themeStyle].primary_white,
                                            }}>
                                            {tag}
                                        </Box>
                                    ))
                                ) : (
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].primary_white,
                                        }}>
                                        No tags
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Event Type
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Event Type"
                                value={editedValues.eventType || ''}
                                onChange={e => handleInputChange('eventType', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: theme[themeStyle].gray_text,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].light_gray_text,
                                    mb: 1,
                                }}>
                                Tags
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {selectedTags.map((tag, index) => (
                                    <Chip
                                        key={index}
                                        label={tag}
                                        onDelete={() => setSelectedTags(selectedTags.filter(t => t !== tag))}
                                        sx={{
                                            backgroundColor: theme[themeStyle].background_gradient.end,
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        }}
                                    />
                                ))}
                            </Box>
                            <Button
                                variant="outlined"
                                startIcon={<AddCircleOutline />}
                                onClick={() => setTagsDialogOpen(true)}
                                sx={{
                                    borderColor: theme[themeStyle].primary_orange,
                                    color: theme[themeStyle].primary_white,
                                    '&:hover': {
                                        borderColor: theme[themeStyle].primary_orange,
                                        backgroundColor: 'rgba(255, 140, 0, 0.1)',
                                    },
                                }}>
                                Add Tags
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </Box>

            {/* Event Schedule */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Event Schedule" section="schedule" />

                {!editingSections.schedule ? (
                    <EventSchedule event={event} />
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {editedValues.event_date_breakdown?.map((breakdown: EventDateBreakdown, index: number) => (
                            <Grid item xs={12} key={index}>
                                <Box
                                    sx={{
                                        p: 2,
                                        border: `1px solid ${theme[themeStyle].form_black}`,
                                        borderRadius: 1,
                                    }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    color: theme[themeStyle].light_gray_text,
                                                    mb: 1,
                                                }}>
                                                Start Date
                                            </Typography>
                                            <CustomDatePicker
                                                value={breakdown.start_date as string}
                                                onChange={date => handleBreakdownChange(index, 'start_date', date)}
                                                minDate={new Date()}
                                                withTime
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    color: theme[themeStyle].light_gray_text,
                                                    mb: 1,
                                                }}>
                                                End Date
                                            </Typography>
                                            <CustomDatePicker
                                                value={breakdown.end_date as string}
                                                onChange={date => handleBreakdownChange(index, 'end_date', date)}
                                                minDate={breakdown.start_date}
                                                withTime
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    color: theme[themeStyle].light_gray_text,
                                                    mb: 1,
                                                }}>
                                                Details
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={2}
                                                value={breakdown.detail || ''}
                                                onChange={e => handleBreakdownChange(index, 'detail', e.target.value)}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        color: theme[themeStyle].tag_white,
                                                        backgroundColor: theme[themeStyle].gray_text,
                                                    },
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                        ))}
                        <Grid item xs={12}>
                            <Button
                                variant="outlined"
                                startIcon={<AddCircleOutline />}
                                onClick={handleAddBreakdown}
                                sx={{
                                    borderColor: theme[themeStyle].primary_orange,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                Add Schedule Item
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </Box>

            {/* Tags Dialog */}
            <Dialog
                open={tagsDialogOpen}
                onClose={() => setTagsDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: theme[themeStyle].pure_black,
                        color: theme[themeStyle].primary_white,
                        width: '100%',
                        maxWidth: 500,
                    },
                }}>
                <Box sx={{ p: 3 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '1.25rem',
                            mb: 2,
                        }}>
                        Select Tags
                    </Typography>

                    {/* Add custom tag */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                        <TextField
                            fullWidth
                            placeholder="Add a custom tag"
                            value={newTag}
                            onChange={e => setNewTag(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: theme[themeStyle].tag_white,
                                    backgroundColor: theme[themeStyle].gray_text,
                                },
                            }}
                            onKeyPress={e => {
                                if (e.key === 'Enter') {
                                    handleAddTag();
                                    e.preventDefault();
                                }
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleAddTag}
                            sx={{
                                backgroundColor: theme[themeStyle].primary_orange,
                                '&:hover': {
                                    backgroundColor: theme[themeStyle].primary_orange,
                                    opacity: 0.9,
                                },
                            }}>
                            Add
                        </Button>
                    </Box>

                    {/* Common tags */}
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: '1rem',
                            mb: 1,
                        }}>
                        Common Tags
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                        {commonTags.map((tag, index) => (
                            <Chip
                                key={index}
                                label={tag}
                                onClick={() => handleSelectTag(tag)}
                                sx={{
                                    backgroundColor: selectedTags.includes(tag)
                                        ? theme[themeStyle].primary_orange
                                        : theme[themeStyle].gray_text,
                                    color: theme[themeStyle].primary_white,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    cursor: 'pointer',
                                }}
                            />
                        ))}
                    </Box>

                    {/* Selected tags */}
                    {selectedTags.length > 0 && (
                        <>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '1rem',
                                    mb: 1,
                                }}>
                                Selected Tags
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                                {selectedTags.map((tag, index) => (
                                    <Chip
                                        key={index}
                                        label={tag}
                                        onDelete={() => setSelectedTags(selectedTags.filter(t => t !== tag))}
                                        sx={{
                                            backgroundColor: theme[themeStyle].background_gradient.end,
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        }}
                                    />
                                ))}
                            </Box>
                        </>
                    )}

                    {/* Actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={() => setTagsDialogOpen(false)}
                            sx={{
                                borderColor: theme[themeStyle].placeholderGray,
                                color: theme[themeStyle].primary_white,
                            }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => setTagsDialogOpen(false)}
                            sx={{
                                backgroundColor: theme[themeStyle].primary_orange,
                                '&:hover': {
                                    backgroundColor: theme[themeStyle].primary_orange,
                                    opacity: 0.9,
                                },
                            }}>
                            Done
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};
